import React, {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import cn from 'classnames';
import DashboardContent from 'components/DashboardContent/styles';
import DashboardHeader from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import ByocProgressBar from 'components/ByocProgressBar';
import {
  ByocOnboardingContainer,
  ByocOnboardingTitle
} from 'routes/App/byocOnboarding/styles';
import {
  IntegrationStatusContainer,
  SuccessIcon,
  SuccessTitle,
  PlatformStatusCard,
  PlatformLogo,
  PlatformStatusContent,
  StatusRow,
  StatusLabel,
  StatusValue,
  StatusText
} from 'routes/App/byocOnboarding/IntegrationStatus/styles';
import {PROGRESS_BAR_STEPS} from 'helpers/constants/byocOnboarding';
import IconUEProvider from 'assets/selfDelivery/icon_ue_provider.svg';
import IconGHProvider from 'assets/selfDelivery/icon_gh_provider.svg';
import IconDDProvider from 'assets/selfDelivery/icon_dd_provider.svg';
import Loading from 'components/shared/Utils/Loading';
import {EmailMarketingSpinner} from 'routes/App/emailMarketing/components/styles';

const ALL_PLATFORMS = [
  {
    id: 'uber',
    name: 'Uber Eats',
    logo: IconUEProvider
  },
  {
    id: 'grubhub',
    name: 'GrubHub',
    logo: IconGHProvider
  },
  {
    id: 'doordash',
    name: 'DoorDash',
    logo: IconDDProvider
  }
];

const IntegrationStatus = () => {
  const history = useHistory();
  const {data, globalLoading} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const orderSourceData =
    data?.orderSourceIntegration || data?.OrderSourceIntegration || {};
  const selectedPlatforms =
    orderSourceData?.selectedPlatforms ||
    orderSourceData?.SelectedPlatforms ||
    [];
  const connectedPlatforms =
    orderSourceData?.connectedPlatforms ||
    orderSourceData?.ConnectedPlatforms ||
    [];
  const paymentDetails = data?.paymentDetails || data?.PaymentDetails || {};
  const isPaymentMethodNotConnected =
    paymentDetails?.paymentMethodConnected === false ||
    paymentDetails?.paymentMethodConnected === undefined;
  const isPaymentDetailsFinished =
    paymentDetails?.isFinished === true || paymentDetails?.IsFinished === true;

  // Show all selected platforms and sort them in specific order
  const platformsToShow = useMemo(() => {
    if (!selectedPlatforms || selectedPlatforms.length === 0) {
      return [];
    }

    // Define the order: uber, grubhub, doordash
    const platformOrder = ['uber', 'grubhub', 'doordash'];

    // Filter and sort platforms according to the defined order
    return ALL_PLATFORMS.filter((platform) =>
      selectedPlatforms.includes(platform.id)
    ).sort((a, b) => {
      const indexA = platformOrder.indexOf(a.id);
      const indexB = platformOrder.indexOf(b.id);
      // If platform not found in order, put it at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [selectedPlatforms]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader>
        <Headline className='mTop5'>Self Delivery</Headline>
      </DashboardHeader>
      {globalLoading ? (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      ) : (
        <>
          <ByocProgressBar currentStep={3} steps={PROGRESS_BAR_STEPS} />
          <IntegrationStatusContainer>
            <SuccessIcon>
              <svg
                width='80'
                height='80'
                viewBox='0 0 80 80'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle cx='40' cy='40' r='40' fill='#E8F5E9' />
                <path
                  d='M25 40L35 50L55 30'
                  stroke='#4CAF50'
                  strokeWidth='4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </SuccessIcon>
            <SuccessTitle>Setup Successful!</SuccessTitle>

            {isPaymentMethodNotConnected && isPaymentDetailsFinished && (
              <StatusText style={{marginBottom: '24px', maxWidth: '600px'}}>
                Please add a payment method to complete your setup.{' '}
                <span
                  onClick={() => {
                    history.push('/byoc-onboarding/payment-details');
                  }}
                  style={{
                    color: '#ff2d38',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Go to Payment Details
                </span>
              </StatusText>
            )}

            {platformsToShow.length > 0 ? (
              platformsToShow.map((platform) => {
                const isConnected = connectedPlatforms.includes(platform.id);
                return (
                  <PlatformStatusCard key={platform.id}>
                    <PlatformLogo
                      className={cn('', {bSize: platform.id === 'doordash'})}
                    >
                      <img src={platform.logo} alt={platform.name} />
                    </PlatformLogo>
                    <PlatformStatusContent>
                      <StatusRow className='connected'>
                        <StatusLabel>Integration:</StatusLabel>
                        <StatusValue $isConnected={isConnected}>
                          {isConnected ? 'Connected' : 'Not Connected'}
                        </StatusValue>
                      </StatusRow>
                      <StatusRow>
                        <StatusLabel>
                          Commercial <br /> Agreement:
                        </StatusLabel>
                        {isConnected ? (
                          <StatusText>
                            Check your email daily to see if you had received an
                            agreement to sign.
                          </StatusText>
                        ) : (
                          <StatusValue $isConnected={isConnected}>
                            Not Connected
                          </StatusValue>
                        )}
                      </StatusRow>
                    </PlatformStatusContent>
                  </PlatformStatusCard>
                );
              })
            ) : (
              <StatusText>No platforms selected.</StatusText>
            )}
          </IntegrationStatusContainer>
        </>
      )}
    </DashboardContent>
  );
};

export default IntegrationStatus;
